// Dossier-scoped state shared by every workspace page: the dossier, its
// documents and readiness (polled while documents are processing), and the
// two overlays any page can open (upload, document inspection).
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { toast as notify } from "sonner";
import { api } from "./api/client";
import { invalidate, useResource, type Resource } from "./api/resource";
import type { Dossier, DossierDocument, InputReadiness } from "./api/types";
import { isActive } from "./status";

const POLL_MS = 2500;

export const keys = {
  documents: (id: string) => `documents:${id}`,
  readiness: (id: string) => `readiness:${id}`,
  audit: (id: string) => `audit:${id}`,
  jobs: (id: string) => `jobs:${id}`,
  document: (id: string) => `document:${id}`,
  sources: (id: string, code: string) => `sources:${id}:${code}`,
  inputs: (id: string, code: string) => `inputs:${id}:${code}`,
  generations: (id: string, code: string) => `generations:${id}:${code}`,
  generationSummary: (id: string) => `generation-summary:${id}`,
  chapterRuns: (id: string, code: string) => `chapter-runs:${id}:${code}`,
  reportAudits: (id: string, lang: string) => `report-audits:${id}:${lang}`,
  review: (id: string, code: string) => `review:${id}:${code}`,
  reviews: (id: string) => `reviews:${id}`,
  report: (id: string, lang: string) => `report:${id}:${lang}`,
  plan: (id: string, lang: string) => `plan:${id}:${lang}`,
  team: (id: string) => `team:${id}`,
  schedule: (id: string) => `schedule:${id}`,
  requests: (id: string, code: string) => `requests:${id}:${code}`,
};

interface DossierContextValue {
  dossier: Dossier;
  documents: Resource<DossierDocument[]>;
  readiness: Resource<InputReadiness>;
  activeCount: number;
  openUpload: (versionOf?: DossierDocument) => void;
  inspect: (documentId: string) => void;
  toast: (message: string) => void;
  /** Refetch everything derived from this dossier's documents. */
  refresh: () => void;
}

const Context = createContext<DossierContextValue | null>(null);

export function useDossier(): DossierContextValue {
  const value = useContext(Context);
  if (!value) throw new Error("useDossier outside DossierProvider");
  return value;
}

export interface OverlayState {
  upload: { open: boolean; versionOf?: DossierDocument };
  inspected: string | null;
}

export function DossierProvider({ dossier, children, overlays }: {
  dossier: Dossier; children: ReactNode; overlays: (state: OverlayState, close: {
    upload: () => void; inspect: () => void; }) => ReactNode;
}) {
  const [upload, setUpload] = useState<OverlayState["upload"]>({ open: false });
  const [inspected, setInspected] = useState<string | null>(null);

  // Poll only while something is processing; the flag follows the latest list.
  const [polling, setPolling] = useState(false);
  const pollMs = polling ? POLL_MS : undefined;
  const documents = useResource(keys.documents(dossier.id), () => api.listDocuments(dossier.id), { pollMs });
  const readiness = useResource(keys.readiness(dossier.id), () => api.inputReadiness(dossier.id), { pollMs });
  const activeCount = (documents.data || []).filter(document => isActive(document.status)).length;
  useEffect(() => setPolling(activeCount > 0), [activeCount]);

  // When the last document finishes, refresh what its result changed once more.
  const wasActive = useRef(false);
  useEffect(() => {
    if (wasActive.current && activeCount === 0) invalidate(keys.readiness(dossier.id), keys.jobs(dossier.id),
      keys.audit(dossier.id), "document:", `datapoints:${dossier.id}`, `inputs:${dossier.id}`);
    wasActive.current = activeCount > 0;
  }, [activeCount, dossier.id]);

  const toast = useCallback((message: string) => { notify(message); }, []);
  const refresh = useCallback(() => invalidate(keys.documents(dossier.id), keys.readiness(dossier.id),
    keys.jobs(dossier.id), keys.audit(dossier.id), "document:", `sources:${dossier.id}`, `inputs:${dossier.id}`,
    `datapoints:${dossier.id}`), [dossier.id]);

  const value: DossierContextValue = {
    dossier, documents, readiness, activeCount, toast, refresh,
    openUpload: versionOf => setUpload({ open: true, versionOf }),
    inspect: id => setInspected(id),
  };
  return <Context.Provider value={value}>
    {children}
    {overlays({ upload, inspected },
      { upload: () => setUpload({ open: false }), inspect: () => setInspected(null) })}
  </Context.Provider>;
}
